import React, { useEffect, useMemo, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  SelectChangeEvent,
} from '@mui/material';
import { IconArrowRight, IconBack } from '../../../icons/Icon';
import SingleSelect from '../../../components/commonComponent/SingleSelect';
import styles from './plansetup.module.scss';
import MultiSelectNew from '../../../components/MultiSelectNew';
import { useNavigate, useLocation } from 'react-router-dom';
import { BASE_URL } from '../../../constants/apiConstants';
import axiosInstance from '../../../api/axios';
import { toast } from 'react-toastify';

const EditRoleConfiguration: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [moduleCode, setModuleCode] = useState<any>(null);
  const [selectedOptions, setSelectedOptions] = useState<{
    sector: string;
    module: string[];
  }>({
    sector: '',
    module: [],
  });

  const moduleCodeList = moduleCode?.data?.listofmodulecodelist;
  const [isCostFieldVisible, setIsCostFieldVisible] = useState('');
  const location = useLocation();
  const segments = location.pathname.split('/');
  const id = segments[3];
  const userId = localStorage.getItem('userid');

  const goBack = () => {
    navigate(-1);
  };

  interface SectorListItem {
    cmpsectorcode: string;
    isselected: boolean;
    [key: string]: any;
  }

  interface SectorModuleListResponse {
    data?: {
      listofsectorlist?: SectorListItem[];
      [key: string]: any;
    };
    [key: string]: any;
  }

  const getSectorModuleListByRoleId = async () => {
    try {
      const res = await axiosInstance.post<SectorModuleListResponse>(
        `${BASE_URL}/api/v1/Role/GetSectorModuleListByRoleId`,
        { roleid: Number(id) }
      );
      setData(res.data);
          // Preselect sector if "isselected" is true
      const selectedSector = res.data?.data?.listofsectorlist?.find((sector: any) => sector.isselected);
      if (selectedSector) {
        setSelectedOptions((prev) => ({
          ...prev,
          sector: selectedSector.cmpsectorcode,
        }));
      }
    } catch (error) {
      toast('Error fetching sector/module list', { type: "error" });
    }
  };

  const mapRolesSelectedModules = async () => {
    try {
      const res = await axiosInstance.post(
        `${BASE_URL}/api/v1/Role/GetModuleByRoleIdSectorId`,
        {
          roleid: Number(id),
          sectorcode: selectedOptions.sector,
        }
      );      
      setModuleCode(res.data);
    } catch (error) {
      setModuleCode(null);
      toast("No modules found for the given RoleId and SectorCode.", { type: "error" });
    }
  };

  useEffect(() => {
    getSectorModuleListByRoleId();
  }, [id]);

  useEffect(() => {
    if (selectedOptions.sector) {
      mapRolesSelectedModules();
    }
  }, [selectedOptions.sector]);
  
  useEffect(() => {
    if (Array.isArray(moduleCodeList) && moduleCodeList.length > 0) {
      const selectedModuleCodes = moduleCodeList.map(String);
      setSelectedOptions((prev) => ({
        ...prev,
        module: selectedModuleCodes,
      }));
    } else {
      setSelectedOptions((prev) => ({
        ...prev,
        module: [],
      }));
    }
  }, [moduleCodeList, selectedOptions.sector]);

  useEffect(() => {
    if (data?.data) {
      setIsCostFieldVisible(data.data.iscostfieldvisible ? 'yes' : 'no');
    }
  }, [data?.data]);

  const moduleOptions = useMemo(() =>
    (data?.data?.listofmodulelist || []).map((item: any) => ({
      label: item.menudisplayname,
      value: item.modulecode,
      code: item.modulecode,
      id: item.modulecode,
    })), [data?.data?.listofmodulelist]);

  const sectorOptions = useMemo(() =>
    (data?.data?.listofsectorlist || []).map((item: any) => ({
      label: item.sectorname,
      value: item.cmpsectorcode,
      code: item.cmpsectorcode,
      id: item.cmpsectorcode,
    })), [data?.data?.listofsectorlist]);

  const handleChange = (field: keyof typeof selectedOptions) => (event: SelectChangeEvent<string | number>) => {
    const value = event.target.value;
    setSelectedOptions((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleMultiSelectChange = (field: keyof typeof selectedOptions, selectedValues: string[]) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [field]: selectedValues,
    }));
  };

  const handleSubmit = async () => {
    const payload = {
      roleid: Number(id),
      cmpsectorcode: selectedOptions.sector,
      modulecodelist: selectedOptions.module,
      iscostfieldvisible: isCostFieldVisible === "yes" ? true : false,
      userId:userId,
    };
    
    try {
      await axiosInstance.post(`${BASE_URL}/api/v1/Role/SaveUpdateRoleSectorModuleMapping`, payload);
      toast("Update Role Module saved successfully", { type: "success" });
      navigate('/roleconfiguration')
    } catch (err) {
      toast("Failed to Update Role Module", { type: "error" });
    }
  };

  return (
    <div className="content-wrapper">
      <h1 className="flex align-items-center">
        <button onClick={goBack} className="mr-10"><IconBack /></button>
        Map Role Configuration: {data?.data?.rolename} 
      </h1>
      <div className="white-box mb-12">
        <div className='inner-contant'>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <FormControl fullWidth>
              <div className="flex justify-content-between align-items-end flex-wrap">
                <div className={`${styles.leftBox} flex flex-wrap justify-content-between align-items-end`}>

                  <div className={`flex flex-column ${styles.multiSelect}`}>
                    <SingleSelect
                      label="Select Sector"
                      options={sectorOptions}
                      value={selectedOptions.sector}
                      onChange={handleChange('sector')}
                      placeholder="Sector"
                    />
                  </div>

                  <div className={`flex flex-column ${styles.multiSelect}`}>
                    <MultiSelectNew
                      label="Select Module"
                      options={moduleOptions}
                      selectedValues={selectedOptions.module}
                      onApply={(selected) => handleMultiSelectChange('module', selected)}
                      showLabelOnly={true} 
                      required={true}
                      placeholder="Module"
                    />
                  </div>

                  <div className={`flex flex-column ${styles.multiSelect}`}>
                    <label className='mb-6'>Cost View</label>
                    <RadioGroup
                      name='row-radio-buttons-group'
                      className='flex flex-row align-items-center'
                      value={isCostFieldVisible}
                      onChange={(e) => setIsCostFieldVisible(e.target.value)}
                    >
                      <FormControlLabel value={'yes'} control={<Radio />} label={'Yes'} className='mr-10' />
                      <FormControlLabel value={'no'} control={<Radio />} label={'No'} />
                    </RadioGroup>
                  </div>
                </div>

                <Button onClick={handleSubmit} className={`primary-btn ${styles.primaryBtn}`}>
                  Go <IconArrowRight />
                </Button>
              </div>
            </FormControl>
          </LocalizationProvider>
        </div>
      </div>
    </div>
  );
};

export default EditRoleConfiguration;
